import moment, { type Moment } from 'moment';
import type { IntlShape } from 'react-intl';

import type {
  FieldValue,
  FilterValues,
  SetValueProps,
} from '@src/components/FilterDropDown/FilterDropDown.interfaces';
import {
  formatAbsolutePeriodForTag,
  formatRelativePeriodForTag,
  createPresetPeriodValue,
  clampRangeToSpan,
  getMaxRelativeAmount,
  isRelativePeriodValue,
  type PeriodMaxSpan,
  type PeriodDateType,
  type PeriodPresetId,
  type RelativeAbsolutePeriodValue,
} from '@src/components/RelativeAbsolutePeriodFilter';
import { clampCalendarRangeToToday, DateUtil } from '@src/packages/utils/dates';
import { translateString } from '@src/util/intl/translateString';

import type {
  ScheduleDashboardDateType,
  ScheduleDashboardFiltersStorage,
  SelectedSchedulesState,
} from './types';

export const SCHEDULES_DASHBOARD_FILTER_FIELDS = {
  RELATIVE: 'schedules.relative',
  DATE_RANGE: 'schedules.dateRange',
  GROUP_BY: 'schedules.groupBy',
  SCHEDULES: 'schedules.selected',
} as const;

export const SCHEDULES_DASHBOARD_FILTERS_STORAGE_KEY = 'schedulesDashboardFilters';

export const SCHEDULE_RELATIVE_DEFAULT = {
  isRelative: true,
  relativeAmount: 1,
  dateType: 'day' as ScheduleDashboardDateType,
};

export const SCHEDULE_GROUP_BY_DEFAULT: ScheduleDashboardDateType = 'day';

/** Mirrors the executions-history contract: report periods never exceed three calendar months. */
export const SCHEDULES_REPORT_MAX_SPAN: PeriodMaxSpan = { amount: 3, unit: 'month' };

export const DEFAULT_DATE_RANGE: [Moment, Moment] = [moment().subtract(1, 'month'), moment()];

export const DEFAULT_SELECTED_SCHEDULES: SelectedSchedulesState = {
  byId: {},
  selectedAll: false,
};

export interface ScheduleRelativeFilter {
  isRelative: boolean;
  relativeAmount: number;
  dateType: ScheduleDashboardDateType;
  periodMode?: 'preset' | 'absolute';
  periodPreset?: PeriodPresetId;
}

export interface ScheduleChartFilters {
  relative: ScheduleRelativeFilter;
  dateRange: [Moment, Moment];
  groupBy: ScheduleDashboardDateType;
  schedules: SelectedSchedulesState;
}

export const buildDefaultScheduleChartFilters = (
  timeZone = DateUtil.getDefaultTimeZone(),
): ScheduleChartFilters => {
  const period = createPresetPeriodValue(
    'currentMonth',
    undefined,
    timeZone,
    SCHEDULES_REPORT_MAX_SPAN,
    true,
  );

  return {
    relative: {
      isRelative: false,
      relativeAmount: 1,
      dateType: 'day',
      periodMode: 'preset',
      periodPreset: 'currentMonth',
    },
    dateRange: period.dateRange,
    groupBy: SCHEDULE_GROUP_BY_DEFAULT,
    schedules: DEFAULT_SELECTED_SCHEDULES,
  };
};

export const isTruthyRelativeMode = (relative: unknown): relative is ScheduleRelativeFilter =>
  relative != null &&
  typeof relative === 'object' &&
  !Array.isArray(relative) &&
  ((relative as ScheduleRelativeFilter).isRelative === true ||
    (relative as { isRelative?: unknown }).isRelative === 'true');

export const limitScheduleFiltersToMaxSpan = (
  filters: ScheduleChartFilters,
  timeZone = DateUtil.getDefaultTimeZone(),
): ScheduleChartFilters => {
  if (filters.relative.isRelative === true) {
    const maxAmount = getMaxRelativeAmount(
      filters.relative.dateType,
      SCHEDULES_REPORT_MAX_SPAN,
    );
    if (maxAmount < 1) {
      return { ...filters, relative: SCHEDULE_RELATIVE_DEFAULT };
    }

    return {
      ...filters,
      relative: {
        ...filters.relative,
        relativeAmount: Math.min(filters.relative.relativeAmount, maxAmount),
      },
    };
  }

  const dateType = filters.relative.dateType === 'year' ? 'day' : filters.relative.dateType;

  return {
    ...filters,
    relative: { ...filters.relative, dateType },
    dateRange: clampRangeToSpan(
      clampCalendarRangeToToday(filters.dateRange, timeZone),
      dateType,
      SCHEDULES_REPORT_MAX_SPAN,
    ),
  };
};

export const normalizeStoredScheduleFilters = (
  raw: ScheduleDashboardFiltersStorage | ScheduleChartFilters | null | undefined,
  timeZone = DateUtil.getDefaultTimeZone(),
): ScheduleChartFilters | null => {
  if (!raw || typeof raw !== 'object') return null;

  if ('relative' in raw && raw.relative && typeof raw.relative === 'object') {
    const relative = { ...SCHEDULE_RELATIVE_DEFAULT, ...raw.relative };
    let dateRange = raw.dateRange;
    if (Array.isArray(dateRange) && dateRange.length === 2) {
      dateRange = [moment(dateRange[0]), moment(dateRange[1])];
    } else {
      dateRange = [...DEFAULT_DATE_RANGE];
    }

    return limitScheduleFiltersToMaxSpan({
      relative,
      dateRange,
      groupBy: raw.groupBy ?? SCHEDULE_GROUP_BY_DEFAULT,
      schedules: raw.schedules ?? DEFAULT_SELECTED_SCHEDULES,
    }, timeZone);
  }

  const legacy = raw as ScheduleDashboardFiltersStorage;
  if (legacy.isRelative == null && legacy.dateType == null) return null;

  const start = legacy.dateRange?.[0];
  const end = legacy.dateRange?.[1];

  return limitScheduleFiltersToMaxSpan({
    relative: {
      isRelative: legacy.isRelative ?? SCHEDULE_RELATIVE_DEFAULT.isRelative,
      relativeAmount: legacy.relativeAmount ?? SCHEDULE_RELATIVE_DEFAULT.relativeAmount,
      dateType: legacy.dateType ?? SCHEDULE_RELATIVE_DEFAULT.dateType,
    },
    dateRange:
      start != null && end != null ? [moment(start), moment(end)] : [...DEFAULT_DATE_RANGE],
    groupBy: legacy.groupBy ?? SCHEDULE_GROUP_BY_DEFAULT,
    schedules: DEFAULT_SELECTED_SCHEDULES,
  }, timeZone);
};

export const filterValuesToScheduleFilters = (
  values: Record<string, { value?: unknown }>,
  timeZone = DateUtil.getDefaultTimeZone(),
): ScheduleChartFilters => {
  const relativeEntry = values[SCHEDULES_DASHBOARD_FILTER_FIELDS.RELATIVE];
  const dateRangeEntry = values[SCHEDULES_DASHBOARD_FILTER_FIELDS.DATE_RANGE];
  const groupByEntry = values[SCHEDULES_DASHBOARD_FILTER_FIELDS.GROUP_BY];
  const schedulesEntry = values[SCHEDULES_DASHBOARD_FILTER_FIELDS.SCHEDULES];

  const rawRelative = relativeEntry?.value;
  let relative: ScheduleRelativeFilter = SCHEDULE_RELATIVE_DEFAULT;
  if (rawRelative === null) {
    relative = { ...SCHEDULE_RELATIVE_DEFAULT, isRelative: false };
  } else if (rawRelative && typeof rawRelative === 'object' && !Array.isArray(rawRelative)) {
    relative = { ...SCHEDULE_RELATIVE_DEFAULT, ...(rawRelative as ScheduleRelativeFilter) };
  }

  let dateRange: [Moment, Moment] = [...DEFAULT_DATE_RANGE];
  const rawDateRange = dateRangeEntry?.value;
  if (Array.isArray(rawDateRange) && rawDateRange.length === 2) {
    dateRange = [moment(rawDateRange[0]), moment(rawDateRange[1])];
  }

  const rawGroupBy = groupByEntry?.value;
  const groupBy =
    rawGroupBy != null && rawGroupBy !== ''
      ? (rawGroupBy as ScheduleDashboardDateType)
      : SCHEDULE_GROUP_BY_DEFAULT;

  const rawSchedules = schedulesEntry?.value;
  const schedules =
    rawSchedules && typeof rawSchedules === 'object' && !Array.isArray(rawSchedules)
      ? (rawSchedules as SelectedSchedulesState)
      : DEFAULT_SELECTED_SCHEDULES;

  return limitScheduleFiltersToMaxSpan({
    relative: {
      ...relative,
      isRelative: isTruthyRelativeMode(relative),
    },
    dateRange,
    groupBy,
    schedules,
  }, timeZone);
};

export const filterValuesToScheduleFiltersUi = (
  values: Record<string, { value?: unknown }>,
  fallback: ScheduleChartFilters,
  timeZone = DateUtil.getDefaultTimeZone(),
): ScheduleChartFilters => {
  const hasScheduleValues = Object.values(SCHEDULES_DASHBOARD_FILTER_FIELDS).some(key =>
    Object.prototype.hasOwnProperty.call(values ?? {}, key),
  );
  if (!hasScheduleValues) {
    return fallback;
  }

  return filterValuesToScheduleFilters(values, timeZone);
};

export const formatGroupByTag = (groupBy: ScheduleDashboardDateType | string, intl: IntlShape) => {
  if (groupBy == null || groupBy === '') return '';
  return intl.formatMessage({ id: `dateTypes.${groupBy}` }, { amount: 1 });
};

export const formatSchedulesForTag = (
  schedules: SelectedSchedulesState | null | undefined,
  intl: IntlShape,
) => {
  if (!schedules) return '';

  if (schedules.selectedAll) {
    return intl.formatMessage({ id: 'general.all' });
  }

  const count = Object.keys(schedules.byId).length;
  if (count === 0) return '';

  return intl.formatMessage({ id: 'dashboards.schedules.selectedCount' }, { count });
};

export const hasSelectedSchedules = (schedules: SelectedSchedulesState | null | undefined) =>
  Boolean(schedules && (schedules.selectedAll || Object.keys(schedules.byId).length > 0));

export const mergeFilterSetValueProps = (
  current: FilterValues,
  props: SetValueProps,
): FilterValues => ({
  ...current,
  [props.key]: {
    ...current[props.key],
    value: props.value,
    ...(props.label !== undefined ? { label: props.label || '' } : {}),
    ...(props.valueTranslated !== undefined
      ? { valueTranslated: props.valueTranslated || '' }
      : {}),
    ...(props.valueType !== undefined ? { valueType: props.valueType } : {}),
    ...(props.clearValue !== undefined ? { clearValue: props.clearValue } : {}),
  },
});

export const getScheduleIdsForApi = (schedules: SelectedSchedulesState): string[] =>
  schedules.selectedAll ? [] : Object.keys(schedules.byId).sort();

export const scheduleSelectionAffectsApiFetch = (
  current: SelectedSchedulesState,
  next: SelectedSchedulesState,
): boolean => getScheduleIdsForApi(current).join(',') !== getScheduleIdsForApi(next).join(',');

export const getScheduleSelectionKey = (schedules: SelectedSchedulesState) => {
  if (schedules.selectedAll) {
    return `all:${Object.keys(schedules.byId).sort().join(',')}`;
  }

  return Object.keys(schedules.byId).sort().join(',');
};

export const areScheduleSelectionsEqual = (
  current: SelectedSchedulesState,
  next: SelectedSchedulesState,
) => getScheduleSelectionKey(current) === getScheduleSelectionKey(next);

export const buildInitScheduleFilterValues = (
  chartFilters: ScheduleChartFilters,
  intl: IntlShape,
): FilterValues => {
  const { relative, dateRange, groupBy, schedules } = chartFilters;
  const isRelative = isTruthyRelativeMode(relative);

  return {
    // Period is one filter split across two fields: only the active mode holds a value, so the
    // active-tags bar always renders a single "date range" tag.
    [SCHEDULES_DASHBOARD_FILTER_FIELDS.RELATIVE]: {
      value: relative as unknown as FieldValue,
      label: translateString('dashboards.schedules.dateRange'),
      valueTranslated: isRelative ? formatRelativePeriodForTag(relative, intl) : '',
      valueType: isRelative ? 'string' as const : 'null' as const,
      clearValue: null,
    },
    [SCHEDULES_DASHBOARD_FILTER_FIELDS.DATE_RANGE]: {
      value: (isRelative ? null : dateRange) as unknown as FieldValue,
      label: translateString('dashboards.schedules.dateRange'),
      valueTranslated: isRelative ? '' : formatAbsolutePeriodForTag(dateRange),
      valueType: 'array' as const,
      clearValue: null,
    },
    [SCHEDULES_DASHBOARD_FILTER_FIELDS.GROUP_BY]: {
      value: groupBy,
      label: translateString('dashboards.schedules.groupResultsBy'),
      valueTranslated: formatGroupByTag(groupBy, intl),
      valueType: 'string' as const,
      clearValue: null,
    },
    [SCHEDULES_DASHBOARD_FILTER_FIELDS.SCHEDULES]: {
      value: (hasSelectedSchedules(schedules) ? schedules : null) as unknown as FieldValue,
      label: translateString('schedules.type.schedule'),
      valueTranslated: formatSchedulesForTag(schedules, intl),
      valueType: 'array' as const,
      clearValue: null,
    },
  };
};

/** Adapts the dashboard filters to the shared period control (relative mode + range in one value). */
export const scheduleFiltersToPeriodValue = (
  chart: ScheduleChartFilters,
): RelativeAbsolutePeriodValue => {
  const { relative, dateRange } = chart;
  const dateType = relative.dateType as PeriodDateType;

  if (relative.isRelative) {
    return { isRelative: true, relativeAmount: relative.relativeAmount, dateType };
  }

  return {
    isRelative: false,
    dateRange,
    dateType,
    mode: relative.periodMode ?? 'absolute',
    presetId: relative.periodPreset,
  };
};

/**
 * Inverse of {@link scheduleFiltersToPeriodValue}. Both fields always travel together and only the
 * active mode carries a value — the inactive one is cleared so the period shows a single tag.
 */
export const periodValueToScheduleFilterProps = (
  period: RelativeAbsolutePeriodValue,
  intl: IntlShape,
): SetValueProps[] => {
  const dateType = (period.dateType ??
    SCHEDULE_RELATIVE_DEFAULT.dateType) as ScheduleDashboardDateType;

  if (isRelativePeriodValue(period)) {
    return [
      buildScheduleFilterSetValueProps(
        SCHEDULES_DASHBOARD_FILTER_FIELDS.RELATIVE,
        { isRelative: true, relativeAmount: period.relativeAmount, dateType },
        intl,
      ),
      buildScheduleFilterSetValueProps(SCHEDULES_DASHBOARD_FILTER_FIELDS.DATE_RANGE, null, intl),
    ];
  }

  return [
    {
      ...buildScheduleFilterSetValueProps(SCHEDULES_DASHBOARD_FILTER_FIELDS.RELATIVE, {
        isRelative: false,
        relativeAmount: 1,
        dateType,
        periodMode: period.mode ?? 'absolute',
        periodPreset: period.mode === 'preset' ? period.presetId ?? 'currentMonth' : undefined,
      }, intl),
      valueType: 'null',
      valueTranslated: '',
    },
    // The dashboard always queries a window, so a cleared range falls back to the default one.
    buildScheduleFilterSetValueProps(
      SCHEDULES_DASHBOARD_FILTER_FIELDS.DATE_RANGE,
      period.dateRange ?? [...DEFAULT_DATE_RANGE],
      intl,
    ),
  ];
};

export const scheduleFiltersToStoragePayload = (chart: ScheduleChartFilters) => ({
  relative: chart.relative,
  dateRange: chart.dateRange.map(date => moment(date).toISOString()),
  groupBy: chart.groupBy,
  schedules: chart.schedules,
});

export const buildScheduleFilterSetValueProps = (
  key: string,
  value: unknown,
  intl: IntlShape,
): SetValueProps => {
  switch (key) {
    case SCHEDULES_DASHBOARD_FILTER_FIELDS.RELATIVE:
      return {
        key,
        value: value as SetValueProps['value'],
        label: translateString('dashboards.schedules.dateRange'),
        valueTranslated: formatRelativePeriodForTag(value, intl),
        valueType: 'string',
        clearValue: null,
      };
    case SCHEDULES_DASHBOARD_FILTER_FIELDS.DATE_RANGE:
      return {
        key,
        value: value as SetValueProps['value'],
        label: translateString('dashboards.schedules.dateRange'),
        valueTranslated: formatAbsolutePeriodForTag(value as [Moment, Moment] | null),
        valueType: 'array',
        clearValue: null,
      };
    case SCHEDULES_DASHBOARD_FILTER_FIELDS.GROUP_BY:
      return {
        key,
        value: value as SetValueProps['value'],
        label: translateString('dashboards.schedules.groupResultsBy'),
        valueTranslated: formatGroupByTag(value as ScheduleDashboardDateType, intl),
        valueType: 'string',
        clearValue: null,
      };
    case SCHEDULES_DASHBOARD_FILTER_FIELDS.SCHEDULES: {
      const schedules = value as SelectedSchedulesState | null;
      return {
        key,
        value: (hasSelectedSchedules(schedules)
          ? schedules
          : null) as unknown as SetValueProps['value'],
        label: translateString('dashboards.schedules'),
        valueTranslated: formatSchedulesForTag(schedules, intl),
        valueType: 'array',
        clearValue: null,
      };
    }
    default:
      return { key, value: value as SetValueProps['value'], valueType: 'string' };
  }
};
